'use client';
import * as React from 'react';
import NextLink from 'next/link';

import { ExpandLess, ExpandMore, LogoutRounded, Menu as MenuIcon } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Link,
  List,
  ListItemButton,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from '@mui/material';
import { headerLinks } from '@/shared/utils';
import { useCurrentPath } from '@/shared/hooks';
import { useServerAuth } from '@/auth';
import { useRouter } from 'next/navigation';
import Collapse from '@mui/material/Collapse';
import HeaderProfileImage from '../profile-image';
import { useProfile } from '@/profile';

interface MobileSubHeaderProps {
  setMobileMenuOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  showLinks: boolean;
}

const MobileSubHeader: React.FC<MobileSubHeaderProps> = ({
  setMobileMenuOpen: setParentMobileMenuOpen,
  showLinks,
}) => {
  const router = useRouter();
  const { user, logout } = useServerAuth(); // Access user data from context
  const { profile } = useProfile(null);

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [, isCurrentPath] = useCurrentPath();
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  const handleMobileClick = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    if (logout) {
      logout().then(() => {
        setMobileMenuOpen(false); // Close mobile menu after logout
      });
    }
  };

  const handleLinkClick = (href: string) => {
    router.push(href); // Navigate to the link
    setMobileMenuOpen(false); // Close the mobile menu
  };

  const headerLinksMemonized = React.useMemo(() => headerLinks(!!user?.email), [user]);

  React.useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <div className="flex gap-x-6">
        {!user?.email && showLinks && (
          <div className="hidden sm:block flex space-x-3">
            <NextLink href="/auth/login">
              <Button
                component="span"
                role="contentinfo"
                variant="outlined"
                color="primary"
                size="small"
              >
                <Typography variant="button-m">Login</Typography>
              </Button>
            </NextLink>
            <NextLink href="/auth/registrazione">
              <Button
                component="span"
                role="contentinfo"
                variant="contained"
                color="primary"
                size="small"
              >
                <Typography variant="button-m">Iscriviti</Typography>
              </Button>
            </NextLink>
          </div>
        )}
        <IconButton edge="start" aria-label="menu" onClick={handleMobileClick}>
          <MenuIcon className="text-text-primary" />
        </IconButton>
      </div>
      {mobileMenuOpen && (
        <Paper
          square={true}
          className="fixed top-[60px] sm:top-[70px] left-0 w-full h-full rounded-none"
        >
          <div className="flex flex-col h-full pb-[60px] sm:pb-[70px] px-2">
            {showLinks && (
              <MenuList className="w-full pt-6 space-y-2 flex-grow">
                {headerLinksMemonized.map((link) => (
                  <MenuItem key={link.href} onClick={() => handleLinkClick(link.href)}>
                    <NextLink key={link.href} style={{ textDecoration: 'none' }} href={link.href}>
                      <Typography
                        sx={{
                          color: isCurrentPath(link.href) ? 'primary.main' : 'primary',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          letterSpacing: '0.5px',
                        }}
                        className="md:text-lg"
                      >
                        {link.label}
                      </Typography>
                    </NextLink>
                  </MenuItem>
                ))}
                {!!user?.email && (
                  <>
                    <MenuItem onClick={handleClick}>
                      <Link
                        underline="none"
                        variant="button-l"
                        color={
                          isCurrentPath('/profilo/me') ||
                          isCurrentPath('/profilo/strumenti') ||
                          isCurrentPath('/profilo/sessioni-studio') ||
                          isCurrentPath('/profilo/acquisti') ||
                          isCurrentPath('/profilo/cambia-password')
                            ? 'primary'
                            : 'textPrimary'
                        }
                      >
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            letterSpacing: '0.5px',
                          }}
                          className="pr-2 md:text-lg"
                        >
                          Il tuo profilo
                          {open ? <ExpandLess /> : <ExpandMore />}
                        </Typography>
                      </Link>
                    </MenuItem>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                      <List component="div" className="space-y-1">
                        <ListItemButton onClick={() => handleLinkClick('/profilo/me')}>
                          <Link
                            underline="none"
                            color={isCurrentPath('/profilo/me') ? 'primary' : 'textPrimary'}
                            variant="button-l"
                            className="md:text-lg"
                          >
                            Profilo
                          </Link>
                        </ListItemButton>
                        <ListItemButton onClick={() => handleLinkClick('/profilo/strumenti')}>
                          <Link
                            underline="none"
                            color={isCurrentPath('/profilo/strumenti') ? 'primary' : 'textPrimary'}
                            variant="button-l"
                            className="md:text-lg"
                          >
                            Strumenti
                          </Link>
                        </ListItemButton>
                        <ListItemButton onClick={() => handleLinkClick('/profilo/sessioni-studio')}>
                          <Link
                            underline="none"
                            color={
                              isCurrentPath('/profilo/sessioni-studio') ? 'primary' : 'textPrimary'
                            }
                            variant="button-l"
                            className="md:text-lg"
                          >
                            Sessioni
                          </Link>
                        </ListItemButton>
                        <ListItemButton onClick={() => handleLinkClick('/profilo/acquisti')}>
                          <Link
                            underline="none"
                            color={isCurrentPath('/profilo/acquisti') ? 'primary' : 'textPrimary'}
                            variant="button-l"
                            className="md:text-lg"
                          >
                            Acquisti
                          </Link>
                        </ListItemButton>
                        <ListItemButton onClick={() => handleLinkClick('/profilo/cambia-password')}>
                          <Link
                            underline="none"
                            color={
                              isCurrentPath('/profilo/cambia-password') ? 'primary' : 'textPrimary'
                            }
                            variant="button-l"
                            className="md:text-lg"
                          >
                            Cambia password
                          </Link>
                        </ListItemButton>
                      </List>
                    </Collapse>
                  </>
                )}
              </MenuList>
            )}
            {!!user?.email ? (
              <div className="px-4 py-6">
                <div className="rounded-lg bg-background flex gap-4 pl-4 pr-2 py-2 justify-between">
                  <div className="flex items-center gap-4">
                    <HeaderProfileImage
                      userEmail={user?.email}
                      userProfilePicture={profile?.profilePicture || null}
                    />
                    {profile?.name ? (
                      <Typography variant="body-400">
                        {profile?.name?.split(' ')?.[0] || ''}
                      </Typography>
                    ) : null}
                  </div>
                  <IconButton edge="start" aria-label="menu" size="large" onClick={handleLogout}>
                    <LogoutRounded sx={{ height: '18px', width: '18px' }} />
                  </IconButton>
                </div>
              </div>
            ) : (
              <div className="block sm:hidden px-4 py-6 flex flex-col gap-y-4">
                <NextLink href="/auth/login">
                  <Button
                    component="span"
                    role="contentinfo"
                    variant="outlined"
                    color="primary"
                    size="medium"
                    className="w-full"
                  >
                    <Typography variant="button-m">Login</Typography>
                  </Button>
                </NextLink>
                <NextLink href="/auth/registrazione">
                  <Button
                    component="span"
                    role="contentinfo"
                    variant="contained"
                    color="primary"
                    size="medium"
                    className="w-full"
                  >
                    <Typography variant="button-m">Iscriviti</Typography>
                  </Button>
                </NextLink>
              </div>
            )}
          </div>
        </Paper>
      )}
    </>
  );
};

export default MobileSubHeader;
